import type {
    CommerceOperation,
    CommerceTransport,
    CommerceTransportContext,
} from "@teqtank/commerce-transport";

export const TEQTANK_COMMERCE_CONTRACT_VERSION = "0.41.0" as const;

/* -------------------------------------------------------------------------- */
/* SDK configuration                                                          */
/* -------------------------------------------------------------------------- */

export interface CommerceSdkIdentity {
    readonly name: "Teqtank Commerce SDK";
    readonly contractVersion: typeof TEQTANK_COMMERCE_CONTRACT_VERSION;
}

export type CommerceProductIdentity = "slug" | "id" | "sku";
export type CommerceDataSource = "playground" | "connected";

export interface CommerceProductDetailConfiguration {
    readonly identity?: CommerceProductIdentity;
    readonly routeTemplate?: string;
    readonly queryParameter?: string;
}

export interface ResolvedCommerceProductDetailConfiguration {
    readonly identity: CommerceProductIdentity;
    readonly routeTemplate: string;
    readonly queryParameter: string;
}

export interface CommerceConfiguration {
    readonly commerceKey: string;
    readonly dataSource: CommerceDataSource;
    readonly productDetail?: CommerceProductDetailConfiguration;
    readonly storefront?: CommerceStorefrontConfiguration;
    readonly transport?: CommerceTransport;
    readonly paymentAdapter?: CommercePaymentAdapter;
    readonly operationMiddleware?: readonly CommerceOperationMiddleware[];
    readonly shareProviders?: readonly CommerceShareProvider[];
    readonly eventListeners?: readonly CommerceEventListener[];
}

export interface ResolvedCommerceConfiguration {
    readonly commerceKey: string;
    readonly dataSource: CommerceDataSource;
    readonly productDetail: ResolvedCommerceProductDetailConfiguration;
    readonly storefront: ResolvedCommerceStorefrontConfiguration;
}

export type CommerceStorefrontAliasResolver = (url: URL) => string | null;

export interface CommerceStorefrontConfiguration {
    readonly resolveAlias?: CommerceStorefrontAliasResolver;
}

export interface ResolvedCommerceStorefrontConfiguration {
    readonly resolveAlias: CommerceStorefrontAliasResolver;
}

export interface CommerceOperationInvocation {
    readonly operation: CommerceOperation<unknown, unknown>;
    readonly request: unknown;
    readonly context: CommerceTransportContext;
}

export type CommerceOperationNext = () => Promise<unknown>;

export type CommerceOperationMiddleware = (
    invocation: CommerceOperationInvocation,
    next: CommerceOperationNext,
) => Promise<unknown>;

/* -------------------------------------------------------------------------- */
/* Session                                                                    */
/* -------------------------------------------------------------------------- */

export interface BootstrapCommerceSessionRequest {
    readonly commerceKey: string;
}

export interface BootstrapCommerceSessionResponse {
    readonly sessionToken: string;
    readonly commerceKey: string;
    readonly dataSource: CommerceDataSource;
}

export interface CommerceSession {
    readonly token: string;
    readonly commerceKey: string;
    readonly dataSource: CommerceDataSource;
}

/* -------------------------------------------------------------------------- */
/* Runtime context                                                            */
/* -------------------------------------------------------------------------- */

export interface BootstrapCommerceContextRequest {
    readonly sessionToken: string;
    readonly commerceKey: string;
}

export interface CommerceTenantContext {
    readonly id: string;
    readonly name: string;
}

export interface CommerceStorefrontContext {
    readonly id: string;
    readonly alias: string;
    readonly name: string;
}

export interface CommerceMarketContext {
    readonly id: string;
    readonly name: string;
    readonly countryCode: string;
}

export interface CommerceCurrencyContext {
    readonly code: string;
    readonly symbol: string;
    readonly decimalPlaces: number;
}

export interface CommerceLocaleContext {
    readonly code: string;
    readonly languageCode: string;
    readonly timeZone: string;
}

export interface CommerceCurrencyOption {
    readonly code: string;
    readonly symbol: string;
    readonly decimalPlaces: number;
}

export interface CommerceCountryOption {
    readonly code: string;
    readonly name: string;
    readonly currencies: readonly CommerceCurrencyOption[];
}

export interface CommerceShippingCountryOption {
    readonly code: string;
    readonly name: string;
}

export type CommerceFeature =
    | "partner-storefront"
    | "coupons"
    | "customer-create"
    | "guest-checkout"
    | "email-verification-code"
    | "email-verification-code-preview"
    | "inventory-visibility"
    | "order-submit"
    | "quick-buy"
    | "rewards"
    | "recurring";

export interface CommerceFeatureSet {
    readonly enabled: readonly CommerceFeature[];
    has(feature: CommerceFeature): boolean;
}

export interface BootstrapCommerceContextResponse {
    readonly tenant: CommerceTenantContext;
    readonly storefront: CommerceStorefrontContext;
    readonly market: CommerceMarketContext;
    readonly currency: CommerceCurrencyContext;
    readonly locale: CommerceLocaleContext;
    readonly countries: readonly CommerceCountryOption[];
    readonly shippingCountries: readonly CommerceShippingCountryOption[];
    readonly features: readonly CommerceFeature[];
}

export interface CommerceContext {
    readonly tenant: CommerceTenantContext;
    readonly storefront: CommerceStorefrontContext;
    readonly market: CommerceMarketContext;
    readonly currency: CommerceCurrencyContext;
    readonly locale: CommerceLocaleContext;
    readonly countries: readonly CommerceCountryOption[];
    readonly shippingCountries: readonly CommerceShippingCountryOption[];
    readonly features: CommerceFeatureSet;
}

export interface SwitchCommerceMarketRequest {
    readonly countryCode: string;
    readonly currencyCode: string;
    readonly confirmCartClear?: boolean;
}

export interface SwitchCommerceMarketResponse {
    readonly context: BootstrapCommerceContextResponse;
    readonly requiresConfirmation: boolean;
    readonly cartCleared: boolean;
}

export interface CommerceMarketSwitchResult {
    readonly context: CommerceContext;
    readonly requiresConfirmation: boolean;
    readonly cartCleared: boolean;
}

export interface CommerceContextChangedEvent {
    readonly previous: CommerceContext;
    readonly current: CommerceContext;
    readonly cartCleared: boolean;
}

export type CommerceContextChangedListener = (event: CommerceContextChangedEvent) => void;

export interface CommerceMarketDomain {
    switch(
        request: SwitchCommerceMarketRequest,
        options?: CommerceOperationOptions,
    ): Promise<CommerceMarketSwitchResult>;
    onChanged(listener: CommerceContextChangedListener): CommerceEventUnsubscribe;
}

/* -------------------------------------------------------------------------- */
/* Shared commerce value objects                                              */
/* -------------------------------------------------------------------------- */

export interface CommerceMoney {
    readonly amount: number;
    readonly currencyCode: string;
    readonly formatted: string;
}

export interface CommerceImage {
    readonly url: string;
    readonly alternativeText: string;
    readonly width?: number;
    readonly height?: number;
}

export interface CommercePageRequest {
    readonly page?: number;
    readonly pageSize?: number;
}

export interface CommercePageResult {
    readonly page: number;
    readonly pageSize: number;
    readonly totalItems: number;
    readonly totalPages: number;
    readonly hasPreviousPage: boolean;
    readonly hasNextPage: boolean;
}

/* -------------------------------------------------------------------------- */
/* Product catalogue                                                         */
/* -------------------------------------------------------------------------- */

export type CommerceProductStatus = "active" | "unavailable" | "coming-soon" | "discontinued";

export type CommerceProductSort =
    | "default"
    | "name-ascending"
    | "name-descending"
    | "price-ascending"
    | "price-descending"
    | "newest";

export interface CommercePrice {
    readonly current: CommerceMoney;
    readonly compareAt?: CommerceMoney;
}

export interface CommerceProductOptionValue {
    readonly id: string;
    readonly label: string;
    /** Values in the controlling option group that make this value available. */
    readonly availableForValueIds?: readonly string[];
}

export interface CommerceProductOptionGroup {
    readonly id: string;
    readonly name: string;
    /** Earlier option group whose selected value controls this group. */
    readonly dependsOnOptionId?: string;
    readonly values: readonly CommerceProductOptionValue[];
}

export interface CommerceRecurringFrequency {
    /** @deprecated Use options and defaultOptionId. */
    readonly allowedDays: readonly number[];
    /** @deprecated Use defaultOptionId. */
    readonly defaultDays: number;
    readonly options: readonly CommerceRecurringFrequencyOption[];
    readonly defaultOptionId: string;
}

export type CommerceRecurringFrequencyUnit = "day" | "month";

export interface CommerceRecurringFrequencyOption {
    readonly id: string;
    readonly unit: CommerceRecurringFrequencyUnit;
    readonly interval: number;
    /** @deprecated Compatibility selector for Agara's day-based contract. */
    readonly days: number;
    readonly description: string;
}

export type CommerceCartPurchaseType = "one-time" | "recurring";

export interface CommercePurchaseOffer {
    readonly purchaseType: CommerceCartPurchaseType;
    readonly price: CommercePrice;
    readonly frequency?: CommerceRecurringFrequency;
}

export interface CommerceProductVariantSelection {
    readonly optionId: string;
    readonly valueId: string;
}

export interface CommerceProductVariant {
    readonly id: string;
    readonly displaySku?: string;
    readonly name: string;
    readonly selections: readonly CommerceProductVariantSelection[];
    readonly offers: readonly CommercePurchaseOffer[];
    readonly available: boolean;
    readonly inventoryQuantity?: number;
}

export type CommerceProductType = "product" | "kit";

export interface CommerceProductComponent {
    readonly productId: string;
    readonly displaySku?: string;
    readonly name: string;
    readonly image: CommerceImage | null;
    /** Quantity represented within the containing product or calculated line. */
    readonly quantity: number;
    readonly components?: readonly CommerceProductComponent[];
}

export interface CommerceProductCardContent {
    readonly id: string;
    readonly label?: string;
    readonly value: string;
    readonly format?: CommerceProductContentFormat;
}

export type CommerceProductDetailSectionDisplay = "body" | "accordion";
export type CommerceProductContentFormat = "plain-text" | "rich-html";

export interface CommerceProductDetailSection {
    readonly id: string;
    readonly title?: string;
    readonly display: CommerceProductDetailSectionDisplay;
    readonly format: CommerceProductContentFormat;
    readonly content: string;
}

export interface CommerceProductSummary {
    readonly id: string;
    readonly displaySku?: string;
    readonly slug: string;
    readonly name: string;
    readonly shortDescription: string;
    readonly status: CommerceProductStatus;
    readonly primaryImage: CommerceImage | null;
    readonly representativeOffers: readonly CommercePurchaseOffer[];
    readonly categoryIds: readonly string[];
    readonly tags: readonly string[];
    readonly productType?: CommerceProductType;
    readonly cardContent?: readonly CommerceProductCardContent[];
    /** Whether Product options must be selected before adding it to the Cart. */
    readonly requiresConfiguration?: boolean;
}

export interface CommerceProduct extends CommerceProductSummary {
    readonly description: string;
    readonly images: readonly CommerceImage[];
    readonly optionGroups: readonly CommerceProductOptionGroup[];
    readonly variants: readonly CommerceProductVariant[];
    readonly defaultVariantId: string;
    readonly extensions: Readonly<Record<string, string>>;
    readonly components?: readonly CommerceProductComponent[];
    readonly detailSections?: readonly CommerceProductDetailSection[];
}

export interface CommerceProductCategory {
    readonly id: string;
    readonly slug: string;
    readonly name: string;
    readonly description?: string;
    readonly parentId: string | null;
    readonly image: CommerceImage | null;
    readonly productCount?: number;
    readonly sortOrder: number;
}

export type CommerceCatalogueExperience = "shop" | "enrollment" | (string & Record<never, never>);

export interface SearchCommerceProductsRequest extends CommercePageRequest {
    readonly experience?: CommerceCatalogueExperience;
    readonly catalogueId?: string;
    readonly query?: string;
    readonly categoryId?: string;
    readonly tags?: readonly string[];
    readonly status?: CommerceProductStatus;
    readonly recurringAvailable?: boolean;
    readonly sort?: CommerceProductSort;
}

export interface SearchCommerceProductsResponse {
    readonly items: readonly CommerceProductSummary[];
    readonly pagination: CommercePageResult;
}

export interface CommerceProductLookup {
    readonly identifier: string;
    readonly identity: CommerceProductIdentity;
}

export interface GetCommerceProductRequest extends CommerceProductLookup {
    readonly experience?: CommerceCatalogueExperience;
    readonly catalogueId?: string;
}

export interface GetCommerceProductResponse {
    readonly product: CommerceProduct | null;
}

export interface GetCommerceProductCategoryRequest {
    readonly categoryId: string;
    readonly experience?: CommerceCatalogueExperience;
    readonly catalogueId?: string;
}

export interface GetCommerceProductCategoryResponse {
    readonly category: CommerceProductCategory | null;
}

export interface GetCommerceProductCategoriesRequest {
    readonly experience?: CommerceCatalogueExperience;
    readonly parentId?: string | null;
    readonly catalogueId?: string;
}

export interface GetCommerceProductCategoriesResponse {
    readonly categories: readonly CommerceProductCategory[];
}

export interface CommerceProductCatalogueRepository {
    search(
        request?: SearchCommerceProductsRequest,
        options?: CommerceOperationOptions,
    ): Promise<SearchCommerceProductsResponse>;

    get(
        identifier: string,
        identity?: CommerceProductIdentity,
        options?: CommerceOperationOptions,
    ): Promise<CommerceProduct>;
    getById(productId: string, options?: CommerceOperationOptions): Promise<CommerceProduct>;
    getBySlug(slug: string, options?: CommerceOperationOptions): Promise<CommerceProduct>;
    getBySku(sku: string, options?: CommerceOperationOptions): Promise<CommerceProduct>;
    getByIdInExperience(
        productId: string,
        experience: CommerceCatalogueExperience,
        options?: CommerceOperationOptions,
    ): Promise<CommerceProduct>;
    getByIdInCatalogue(
        catalogueId: string,
        productId: string,
        experience: CommerceCatalogueExperience,
        options?: CommerceOperationOptions,
    ): Promise<CommerceProduct>;

    getCategoryById(
        categoryId: string,
        options?: CommerceOperationOptions,
    ): Promise<CommerceProductCategory>;
    getCategoryByIdInCatalogue(
        catalogueId: string,
        categoryId: string,
        experience: CommerceCatalogueExperience,
        options?: CommerceOperationOptions,
    ): Promise<CommerceProductCategory>;

    getCategories(
        parentId?: string | null,
        options?: CommerceOperationOptions,
    ): Promise<readonly CommerceProductCategory[]>;
    getCategoriesInCatalogue(
        catalogueId: string,
        experience: CommerceCatalogueExperience,
        parentId?: string | null,
        options?: CommerceOperationOptions,
    ): Promise<readonly CommerceProductCategory[]>;

    clearCache(): void;
}

export interface CommerceProductCatalogue extends CommerceProductCatalogueRepository {
    getDetailUrl(product: CommerceProductSummary): string;
    resolveDetailLookup(location?: string | URL): CommerceProductLookup | null;
}

/* -------------------------------------------------------------------------- */
/* Cart                                                                        */
/* -------------------------------------------------------------------------- */

export interface CommerceCartLine {
    readonly id: string;
    readonly productId: string;
    readonly variantId: string;
    readonly displaySku?: string;
    readonly name: string;
    readonly image: CommerceImage | null;
    readonly purchaseType: CommerceCartPurchaseType;
    readonly frequency?: CommerceRecurringFrequencyOption;
    /** @deprecated Use frequency. */
    readonly frequencyDays?: number;
    readonly quantity: number;
    readonly unitPrice: CommerceMoney;
    readonly lineSubtotal: CommerceMoney;
    readonly lineDiscount: CommerceMoney;
    readonly lineTotal: CommerceMoney;
    readonly components?: readonly CommerceProductComponent[];
}

export interface CommerceCartCoupon {
    readonly code: string;
    readonly description: string;
    readonly discount: CommerceMoney;
}

export interface CommerceCartTotals {
    readonly itemCount: number;
    readonly subtotal: CommerceMoney;
    readonly discount: CommerceMoney;
    readonly shipping: CommerceMoney;
    readonly tax: CommerceMoney;
    readonly total: CommerceMoney;
}

export interface CommerceCart {
    readonly id: string;
    readonly version: number;
    readonly currencyCode: string;
    readonly lines: readonly CommerceCartLine[];
    readonly coupons: readonly CommerceCartCoupon[];
    readonly totals: CommerceCartTotals;
    readonly updatedAt: Date;
}

export interface GetCommerceCartRequest {}
export interface GetCommerceCartResponse {
    readonly cart: CommerceCart;
}

export interface AddCommerceCartLineRequest {
    readonly productId: string;
    readonly variantId?: string;
    readonly quantity: number;
    readonly purchaseType?: CommerceCartPurchaseType;
    readonly frequencyId?: string;
    /** @deprecated Use frequencyId. */
    readonly frequencyDays?: number;
    readonly expectedVersion?: number;
    /** Server-registered catalogue experience used to resolve this Product safely. */
    readonly experience?: CommerceCatalogueExperience;
    /** Replace the current Cart atomically when the Product is accepted. */
    readonly replaceCart?: boolean;
}
export interface AddCommerceCartLineResponse {
    readonly cart: CommerceCart;
}

export interface UpdateCommerceCartLineQuantityRequest {
    readonly lineId: string;
    readonly quantity: number;
    readonly expectedVersion?: number;
}
export interface UpdateCommerceCartLineQuantityResponse {
    readonly cart: CommerceCart;
}

export interface RemoveCommerceCartLineRequest {
    readonly lineId: string;
    readonly expectedVersion?: number;
}
export interface RemoveCommerceCartLineResponse {
    readonly cart: CommerceCart;
}

export interface ClearCommerceCartRequest {
    readonly expectedVersion?: number;
}
export interface ClearCommerceCartResponse {
    readonly cart: CommerceCart;
}

export interface ApplyCommerceCartCouponRequest {
    readonly code: string;
    readonly expectedVersion?: number;
}
export interface ApplyCommerceCartCouponResponse {
    readonly cart: CommerceCart;
}

export interface RemoveCommerceCartCouponRequest {
    readonly code: string;
    readonly expectedVersion?: number;
}
export interface RemoveCommerceCartCouponResponse {
    readonly cart: CommerceCart;
}

export type CommerceCartChangeReason =
    | "loaded"
    | "enrollment-synchronized"
    | "checkout-synchronized"
    | "line-added"
    | "line-updated"
    | "line-removed"
    | "cleared"
    | "coupon-applied"
    | "coupon-removed"
    | "share-accepted";

export interface CommerceCartChangedEvent {
    readonly reason: CommerceCartChangeReason;
    readonly cart: CommerceCart;
}

export type CommerceCartChangedListener = (event: CommerceCartChangedEvent) => void;

export interface CommerceCartDomain {
    get(options?: CommerceOperationOptions): Promise<CommerceCart>;
    add(
        request: AddCommerceCartLineRequest,
        options?: CommerceOperationOptions,
    ): Promise<CommerceCart>;
    updateQuantity(
        request: UpdateCommerceCartLineQuantityRequest,
        options?: CommerceOperationOptions,
    ): Promise<CommerceCart>;
    remove(lineId: string, options?: CommerceOperationOptions): Promise<CommerceCart>;
    clear(options?: CommerceOperationOptions): Promise<CommerceCart>;
    applyCoupon(code: string, options?: CommerceOperationOptions): Promise<CommerceCart>;
    removeCoupon(code: string, options?: CommerceOperationOptions): Promise<CommerceCart>;
    onChanged(listener: CommerceCartChangedListener): CommerceEventUnsubscribe;
}

/* -------------------------------------------------------------------------- */
/* Checkout                                                                    */
/* -------------------------------------------------------------------------- */

export interface CommerceAddress {
    readonly firstName: string;
    readonly lastName: string;
    readonly company?: string;
    readonly addressLine1: string;
    readonly addressLine2?: string;
    readonly city: string;
    readonly region: string;
    readonly postalCode: string;
    readonly countryCode: string;
    readonly phone?: string;
}

export interface CommerceAddressRegion {
    readonly code: string;
    readonly name: string;
}

export interface GetCommerceAddressRegionsRequest {
    readonly countryCode: string;
}

export interface GetCommerceAddressRegionsResponse {
    readonly regions: readonly CommerceAddressRegion[];
}

export interface CommerceCheckoutContact {
    readonly email: string;
    readonly phone: string;
    readonly acceptsMarketing: boolean;
}

export interface CommerceShippingMethod {
    readonly id: string;
    readonly name: string;
    readonly description: string;
    readonly estimatedDelivery: string;
    readonly price: CommerceMoney;
    readonly selected: boolean;
}

export type CommerceCheckoutValidationSeverity = "error" | "warning";

export interface CommerceCheckoutValidationMessage {
    readonly code: string;
    readonly message: string;
    readonly field?: string;
    readonly severity: CommerceCheckoutValidationSeverity;
}

export interface CommerceCheckoutValidation {
    readonly isValid: boolean;
    readonly hasErrors: boolean;
    readonly errors: readonly CommerceCheckoutValidationMessage[];
    readonly warnings: readonly CommerceCheckoutValidationMessage[];
    /** Gateway-normalized replacement proposed by authoritative address validation. */
    readonly suggestedAddress?: CommerceAddress;
}

export type CommerceCheckoutStatus =
    "draft" | "ready" | "payment-required" | "completed" | "resolved" | "accepted";

export type CommerceCheckoutItemSource = "cart" | "promotion";

export interface CommerceCheckoutItem {
    readonly id: string;
    readonly productId: string;
    readonly variantId: string;
    readonly displaySku?: string;
    readonly name: string;
    readonly image: CommerceImage | null;
    readonly purchaseType: CommerceCartPurchaseType;
    readonly frequency?: CommerceRecurringFrequencyOption;
    /** @deprecated Use frequency. */
    readonly frequencyDays?: number;
    readonly quantity: number;
    readonly unitPrice: CommerceMoney;
    readonly lineSubtotal: CommerceMoney;
    readonly lineDiscount: CommerceMoney;
    readonly lineTotal: CommerceMoney;
    readonly source: CommerceCheckoutItemSource;
    readonly purchaseTypeLocked?: boolean;
    readonly purchaseTypeLockReason?: string | null;
    /** Gateway-authoritative purchase choices for this line in the active Checkout experience. */
    readonly purchaseOptions?: readonly CommercePurchaseOffer[];
    readonly promotionCode?: string;
    readonly components?: readonly CommerceProductComponent[];
}

export interface CommerceCheckoutUpsellChoice {
    readonly id: string;
    readonly label: string;
    readonly purchaseType: CommerceCartPurchaseType;
    readonly frequency?: CommerceRecurringFrequencyOption;
    /** @deprecated Use frequency. */
    readonly frequencyDays?: number;
    readonly price: CommerceMoney;
    readonly saving?: CommerceMoney;
}

export interface CommerceCheckoutUpsell {
    readonly id: string;
    readonly productId: string;
    readonly variantId?: string;
    readonly name: string;
    readonly description: string;
    readonly image: CommerceImage | null;
    readonly badge?: string;
    readonly choices: readonly CommerceCheckoutUpsellChoice[];
}

export interface CommerceCheckoutTotals {
    readonly subtotal: CommerceMoney;
    readonly discount: CommerceMoney;
    readonly shipping: CommerceMoney;
    readonly itemTax: CommerceMoney;
    readonly shippingTax: CommerceMoney;
    readonly tax: CommerceMoney;
    readonly total: CommerceMoney;
}

export type CommerceCheckoutExperience = "shop" | "enrollment";

export interface CommerceCheckout {
    readonly id: string;
    readonly version: number;
    readonly status: CommerceCheckoutStatus;
    /** Gateway-authored purchase journey that owns this Checkout. */
    readonly experience: CommerceCheckoutExperience;
    readonly cart: CommerceCart;
    readonly contact: CommerceCheckoutContact | null;
    readonly shippingAddress: CommerceAddress | null;
    readonly billingAddress: CommerceAddress | null;
    readonly billingAddressSameAsShipping: boolean;
    readonly shippingMethods: readonly CommerceShippingMethod[];
    readonly selectedShippingMethodId: string | null;
    readonly selectedSavedPaymentMethodId?: string | null;
    /**
     * Authoritative calculated Checkout items returned by the Gateway.
     * These may differ from Cart lines when promotions, free items or
     * other server-side calculation adjustments are applied.
     */
    readonly items: readonly CommerceCheckoutItem[];
    readonly upsells: readonly CommerceCheckoutUpsell[];
    readonly totals: CommerceCheckoutTotals;
    readonly validation: CommerceCheckoutValidation;
    readonly updatedAt: Date;
}

export interface BeginCommerceCheckoutRequest {}
export interface BeginCommerceCheckoutResponse {
    readonly checkout: CommerceCheckout;
}
export interface GetCommerceCheckoutRequest {}
export interface GetCommerceCheckoutResponse {
    readonly checkout: CommerceCheckout | null;
}

export interface UpdateCommerceCheckoutContactRequest {
    readonly contact: CommerceCheckoutContact;
    readonly expectedVersion?: number;
}
export interface UpdateCommerceCheckoutContactResponse {
    readonly checkout: CommerceCheckout;
}

export interface ClearCommerceCheckoutCustomerDetailsRequest {
    readonly expectedVersion?: number;
}
export interface ClearCommerceCheckoutCustomerDetailsResponse {
    readonly checkout: CommerceCheckout;
}

export interface BeginCommerceQuickBuyRequest {
    readonly productId: string;
    readonly variantId?: string;
    readonly quantity: number;
    readonly purchaseType?: CommerceCartPurchaseType;
    readonly frequencyId?: string;
    /** @deprecated Use frequencyId. */
    readonly frequencyDays?: number;
}
export interface BeginCommerceQuickBuyResponse {
    readonly checkout: CommerceCheckout;
}

export interface UpdateCommerceCheckoutAddressRequest {
    readonly address: CommerceAddress;
    readonly addressType: "shipping" | "billing";
    readonly billingAddressSameAsShipping?: boolean;
    readonly expectedVersion?: number;
}
export interface UpdateCommerceCheckoutAddressResponse {
    readonly checkout: CommerceCheckout;
}

export interface SelectCommerceCheckoutShippingMethodRequest {
    readonly shippingMethodId: string;
    readonly expectedVersion?: number;
}
export interface SelectCommerceCheckoutShippingMethodResponse {
    readonly checkout: CommerceCheckout;
}

export interface ApplyCommerceCheckoutCustomerRequest {
    readonly expectedVersion?: number;
}
export interface ApplyCommerceCheckoutCustomerResponse {
    readonly checkout: CommerceCheckout;
}
export interface SelectCommerceCheckoutSavedPaymentMethodRequest {
    readonly paymentMethodId: string | null;
    readonly expectedVersion?: number;
}
export interface SelectCommerceCheckoutSavedPaymentMethodResponse {
    readonly checkout: CommerceCheckout;
}

export interface CommerceCheckoutPaymentOption {
    readonly id: string;
    readonly name: string;
    readonly description?: string;
    readonly recurringSupported: boolean;
}

export interface CommerceCheckoutPoints {
    readonly available: CommerceMoney;
    readonly applied: CommerceMoney;
    readonly enabled: boolean;
}

export interface CommerceCheckoutPaymentConfiguration {
    readonly options: readonly CommerceCheckoutPaymentOption[];
    readonly selectedPaymentOptionId: string | null;
    readonly selectedSavedPaymentMethodId: string | null;
    readonly frameSource: string | null;
    readonly points: CommerceCheckoutPoints;
    readonly amountDue: CommerceMoney;
}

export interface GetCommerceCheckoutPaymentConfigurationRequest {}
export interface GetCommerceCheckoutPaymentConfigurationResponse {
    readonly payment: CommerceCheckoutPaymentConfiguration;
}
export interface SelectCommerceCheckoutPaymentOptionRequest {
    readonly paymentOptionId: string | null;
    readonly savedPaymentMethodId: string | null;
    readonly expectedVersion?: number;
}
export interface SelectCommerceCheckoutPaymentOptionResponse {
    readonly checkout: CommerceCheckout;
    readonly payment: CommerceCheckoutPaymentConfiguration;
}
export interface ApplyCommerceCheckoutPointsRequest {
    readonly apply: boolean;
    readonly expectedVersion?: number;
}
export interface ApplyCommerceCheckoutPointsResponse {
    readonly checkout: CommerceCheckout;
    readonly payment: CommerceCheckoutPaymentConfiguration;
}

export interface CalculateCommerceCheckoutRequest {
    readonly expectedVersion?: number;
    readonly bypassAddressValidation?: boolean;
}
export interface CalculateCommerceCheckoutResponse {
    readonly checkout: CommerceCheckout;
}

export interface RespondCommerceCheckoutUpsellRequest {
    readonly upsellId: string;
    readonly choiceId?: string;
    readonly accepted: boolean;
    readonly expectedVersion?: number;
}
export interface RespondCommerceCheckoutUpsellResponse {
    readonly checkout: CommerceCheckout;
}

export interface UpdateCommerceCheckoutItemPurchaseRequest {
    readonly lineId: string;
    readonly purchaseType: CommerceCartPurchaseType;
    readonly frequencyId?: string;
    /** @deprecated Use frequencyId. */
    readonly frequencyDays?: number;
    readonly expectedVersion?: number;
}
export interface UpdateCommerceCheckoutItemPurchaseResponse {
    readonly checkout: CommerceCheckout;
}

export interface SubmitCommerceCheckoutRequest {
    readonly expectedVersion?: number;
    readonly consents?: readonly CommerceCustomerConsentSelection[];
    readonly customFields?: Readonly<Record<string, string>>;
}
export interface SubmitCommerceCheckoutResponse {
    readonly checkout: CommerceCheckout;
    readonly attemptId: string;
    readonly paymentOrder: unknown;
}
export interface CompleteCommerceCheckoutRequest {
    readonly attemptId: string;
    readonly paymentResult: unknown;
}
export interface CompleteCommerceCheckoutResponse {
    readonly order: CommerceOrder;
    readonly redirectUrl: string | null;
    readonly warnings: readonly string[];
}

export type CommerceCheckoutChangeReason =
    | "begun"
    | "loaded"
    | "contact-updated"
    | "customer-details-cleared"
    | "quick-buy-begun"
    | "address-updated"
    | "shipping-selected"
    | "coupon-applied"
    | "coupon-removed"
    | "customer-applied"
    | "payment-method-selected"
    | "payment-option-selected"
    | "points-applied"
    | "calculated"
    | "upsell-responded"
    | "purchase-updated";

export interface CommerceCheckoutChangedEvent {
    readonly reason: CommerceCheckoutChangeReason;
    readonly checkout: CommerceCheckout;
}

export type CommerceCheckoutChangedListener = (event: CommerceCheckoutChangedEvent) => void;

export interface CommerceCheckoutDomain {
    readonly current: CommerceCheckout | undefined;
    getAddressRegions(
        countryCode: string,
        options?: CommerceOperationOptions,
    ): Promise<readonly CommerceAddressRegion[]>;
    begin(options?: CommerceOperationOptions): Promise<CommerceCheckout>;
    get(options?: CommerceOperationOptions): Promise<CommerceCheckout | null>;
    updateContact(
        contact: CommerceCheckoutContact,
        options?: CommerceOperationOptions,
    ): Promise<CommerceCheckout>;
    clearCustomerDetails(options?: CommerceOperationOptions): Promise<CommerceCheckout>;
    beginQuickBuy(
        request: BeginCommerceQuickBuyRequest,
        options?: CommerceOperationOptions,
    ): Promise<CommerceCheckout>;
    updateShippingAddress(
        address: CommerceAddress,
        options?: CommerceOperationOptions,
    ): Promise<CommerceCheckout>;
    updateBillingAddress(
        address: CommerceAddress,
        options?: CommerceOperationOptions,
    ): Promise<CommerceCheckout>;
    useShippingAddressForBilling(options?: CommerceOperationOptions): Promise<CommerceCheckout>;
    selectShippingMethod(
        shippingMethodId: string,
        options?: CommerceOperationOptions,
    ): Promise<CommerceCheckout>;
    applyCoupon(code: string, options?: CommerceOperationOptions): Promise<CommerceCheckout>;
    removeCoupon(code: string, options?: CommerceOperationOptions): Promise<CommerceCheckout>;
    applyCustomer(options?: CommerceOperationOptions): Promise<CommerceCheckout>;
    selectSavedPaymentMethod(
        paymentMethodId: string | null,
        options?: CommerceOperationOptions,
    ): Promise<CommerceCheckout>;
    getPaymentConfiguration(
        options?: CommerceOperationOptions,
    ): Promise<CommerceCheckoutPaymentConfiguration>;
    selectPaymentOption(
        paymentOptionId: string | null,
        savedPaymentMethodId?: string | null,
        options?: CommerceOperationOptions,
    ): Promise<CommerceCheckoutPaymentConfiguration>;
    applyPoints(
        apply: boolean,
        options?: CommerceOperationOptions,
    ): Promise<CommerceCheckoutPaymentConfiguration>;
    calculate(options?: CommerceOperationOptions): Promise<CommerceCheckout>;
    bypassAddressValidation(options?: CommerceOperationOptions): Promise<CommerceCheckout>;
    respondToUpsell(
        upsellId: string,
        accepted: boolean,
        choiceId?: string,
        options?: CommerceOperationOptions,
    ): Promise<CommerceCheckout>;
    updateItemPurchase(
        lineId: string,
        purchaseType: CommerceCartPurchaseType,
        frequency?: number | CommerceRecurringFrequencyOption,
        options?: CommerceOperationOptions,
    ): Promise<CommerceCheckout>;
    submit(options?: CommerceOperationOptions): Promise<CommerceCheckout>;
    onChanged(listener: CommerceCheckoutChangedListener): CommerceEventUnsubscribe;
}

/* -------------------------------------------------------------------------- */
/* Payment                                                                     */
/* -------------------------------------------------------------------------- */

export type CommercePaymentStatus =
    "unconfigured" | "ready" | "processing" | "succeeded" | "failed" | "cancelled" | "disposed";

export interface CommercePaymentAdapterResult {
    readonly response: unknown;
}

export type CommercePaymentAdapterEventName = "success" | "error";
export type CommercePaymentAdapterListener = (data: unknown) => void;

/** Stable boundary implemented by the UMD PaymentHandler adapter. */
export interface CommercePaymentAdapter {
    readonly name: string;
    createOrder(order: unknown): Promise<CommercePaymentAdapterResult>;
    setBilling(address: CommerceAddress): void;
    clearBilling(): void;
    setPaymentWindow?(): void;
    unsetPaymentWindow?(): void;
    on?(event: CommercePaymentAdapterEventName, listener: CommercePaymentAdapterListener): void;
    off?(event: CommercePaymentAdapterEventName, listener: CommercePaymentAdapterListener): void;
    dispose(): void;
}

export interface CommercePaymentOrder {
    readonly checkoutId: string;
    readonly checkoutVersion: number;
    readonly cartId: string;
    readonly commerceKey: string;
    readonly sessionToken: string;
    readonly currencyCode: string;
    readonly amount: number;
    readonly checkout: CommerceCheckout;
    readonly savedPaymentMethod: CommerceSavedPaymentMethod | null;
    readonly metadata: Readonly<Record<string, unknown>>;
}

export interface SubmitCommercePaymentOrderRequest {
    /** @deprecated Browser metadata is never merged into the authoritative provider order. */
    readonly metadata?: Readonly<Record<string, unknown>>;
    readonly consents?: readonly CommerceCustomerConsentSelection[];
    readonly customFields?: Readonly<Record<string, string>>;
    /** Explicit billing value to synchronize to the payment frame before submission. */
    readonly billingAddress?: CommerceAddress | null;
}

export interface CommercePaymentResult {
    readonly status: "succeeded";
    readonly checkoutId: string;
    readonly response: unknown;
    readonly order: CommerceOrder;
    readonly redirectUrl: string | null;
    readonly warnings: readonly string[];
    readonly completedAt: Date;
}

export interface CommercePaymentState {
    readonly status: CommercePaymentStatus;
    readonly adapterName: string | null;
    readonly attempt: number;
    readonly error: string | null;
    readonly result: CommercePaymentResult | null;
}

export type CommercePaymentChangeReason =
    "configured" | "started" | "succeeded" | "failed" | "cancelled" | "disposed";

export interface CommercePaymentChangedEvent {
    readonly reason: CommercePaymentChangeReason;
    readonly state: CommercePaymentState;
}

export type CommercePaymentChangedListener = (event: CommercePaymentChangedEvent) => void;

export interface CommercePaymentDomain {
    readonly state: CommercePaymentState;
    configure(adapter: CommercePaymentAdapter): CommercePaymentState;
    setBilling(address: CommerceAddress | null): void;
    submitOrder(request?: SubmitCommercePaymentOrderRequest): Promise<CommercePaymentResult>;
    retry(request?: SubmitCommercePaymentOrderRequest): Promise<CommercePaymentResult>;
    cancel(): CommercePaymentState;
    onChanged(listener: CommercePaymentChangedListener): CommerceEventUnsubscribe;
}

/* -------------------------------------------------------------------------- */
/* Orders                                                                      */
/* -------------------------------------------------------------------------- */

export type CommerceOrderStatus = "pending" | "processing" | "completed" | "failed" | "cancelled";

export interface CommerceOrderItem {
    readonly id: string;
    readonly productId: string;
    readonly variantId: string;
    readonly displaySku?: string;
    readonly name: string;
    readonly image: CommerceImage | null;
    readonly quantity: number;
    readonly unitPrice: CommerceMoney;
    readonly lineTotal: CommerceMoney;
}

export interface CommerceOrderTotals {
    readonly subtotal: CommerceMoney;
    readonly discount: CommerceMoney;
    readonly shipping: CommerceMoney;
    readonly tax: CommerceMoney;
    readonly total: CommerceMoney;
}

export interface CommerceShipment {
    readonly id: string;
    readonly status: "pending" | "preparing" | "shipped" | "delivered";
    readonly methodName: string;
    readonly estimatedDelivery: string | null;
    readonly trackingNumber: string | null;
    readonly trackingUrl: string | null;
}

export interface CommerceReceipt {
    readonly id: string;
    readonly number: string;
    readonly issuedAt: Date;
    readonly downloadUrl: string | null;
}

export interface CommerceOrder {
    readonly id: string;
    readonly orderNumber: string;
    readonly status: CommerceOrderStatus;
    readonly currencyCode: string;
    readonly email: string;
    readonly items: readonly CommerceOrderItem[];
    readonly totals: CommerceOrderTotals;
    readonly shippingAddress: CommerceAddress | null;
    readonly billingAddress: CommerceAddress | null;
    readonly shipments: readonly CommerceShipment[];
    readonly receipt: CommerceReceipt | null;
    readonly paymentSummary: string | null;
    readonly failureMessage: string | null;
    readonly createdAt: Date;
    readonly updatedAt: Date;
    readonly completedAt: Date | null;
}

export interface GetCommerceOrderRequest {
    readonly orderId: string;
}
export interface GetCommerceOrderResponse {
    readonly order: CommerceOrder | null;
}
export interface RefreshCommerceOrderRequest {
    readonly orderId: string;
}
export interface RefreshCommerceOrderResponse {
    readonly order: CommerceOrder | null;
}

export interface CommerceOrderWaitOptions extends CommerceOperationOptions {
    readonly intervalMilliseconds?: number;
    readonly timeoutMilliseconds?: number;
}

export type CommerceOrderChangeReason = "loaded" | "updated" | "completed" | "failed";
export interface CommerceOrderChangedEvent {
    readonly reason: CommerceOrderChangeReason;
    readonly order: CommerceOrder;
}
export type CommerceOrderChangedListener = (event: CommerceOrderChangedEvent) => void;

export interface CommerceOrderDomain {
    get(orderId: string, options?: CommerceOperationOptions): Promise<CommerceOrder>;
    refresh(orderId: string, options?: CommerceOperationOptions): Promise<CommerceOrder>;
    waitForCompletion(orderId: string, options?: CommerceOrderWaitOptions): Promise<CommerceOrder>;
    cancelPolling(): void;
    onChanged(listener: CommerceOrderChangedListener): CommerceEventUnsubscribe;
}

/* -------------------------------------------------------------------------- */
/* Storefront customer session                                                */
/* -------------------------------------------------------------------------- */

export type CommerceCustomerSessionStatus =
    "anonymous" | "authenticating" | "authenticated" | "expired";

export interface CommerceSavedPaymentMethod {
    readonly id: string;
    readonly token: string;
    readonly type: string;
    readonly displayName: string;
    readonly cardholderName?: string;
    readonly brand?: string;
    readonly lastFour?: string;
    readonly expiryMonth?: number;
    readonly expiryYear?: number;
    readonly isDefault: boolean;
}

export interface CommerceCustomerAddress {
    readonly id: string;
    readonly type: "shipping" | "mailing" | "billing" | "other";
    readonly label: string;
    readonly address: CommerceAddress;
    readonly isDefaultShipping: boolean;
    readonly isDefaultBilling: boolean;
}

export interface CommerceCustomer {
    readonly id: string;
    readonly email: string;
    readonly firstName: string;
    readonly lastName: string;
    readonly displayName: string;
    readonly initials: string;
    readonly phone?: string;
    readonly primaryShippingAddress?: CommerceAddress;
    readonly billingAddress?: CommerceAddress;
    readonly addresses: readonly CommerceCustomerAddress[];
    readonly savedPaymentMethods?: readonly CommerceSavedPaymentMethod[];
}

export interface CommerceCustomerSession {
    readonly status: CommerceCustomerSessionStatus;
    readonly customer: CommerceCustomer | null;
    readonly authenticatedAt: Date | null;
    readonly expiresAt: Date | null;
    readonly rememberMe: boolean;
    readonly backOfficeUrl: string | null;
}

export type CommerceCustomerIdentityStatus = "new-customer" | "authentication-required";
export type CommerceCustomerIdentityField = "email" | "phone";

export interface ResolveCommerceCustomerIdentityRequest {
    readonly email: string;
    readonly phone: string;
    readonly fields: readonly CommerceCustomerIdentityField[];
}
export interface ResolveCommerceCustomerIdentityResponse {
    readonly status: CommerceCustomerIdentityStatus;
}
export interface CreateCommerceCustomerRequest {
    readonly email: string;
    readonly phone: string;
    readonly firstName: string;
    readonly lastName: string;
    readonly password?: string;
    readonly passwordRequired?: boolean;
    readonly sponsor?: CommerceCustomerSponsorSelection;
    readonly verification?: CommerceCustomerVerification;
    readonly consents?: readonly CommerceCustomerConsentSelection[];
    readonly customFields?: Readonly<Record<string, string>>;
}
export interface CreateCommerceCustomerResponse {
    readonly session: CommerceCustomerSession;
}

export interface CommerceCustomerVerification {
    readonly provider: string;
    readonly token: string;
}
export interface CommerceCustomerConsentSelection {
    readonly id: string;
    readonly accepted: boolean;
}
export interface CommerceCustomerSponsorSelection {
    readonly webAlias?: string;
    readonly noSponsor?: boolean;
}
export interface CommerceCustomerRegistrationVerification {
    readonly provider: "none" | "recaptcha" | string;
    readonly siteKey: string | null;
}
export interface CommerceCustomerRegistrationSponsor {
    readonly mode: "required" | "optional" | "hidden";
    readonly allowNoSponsor: boolean;
    readonly attributionApplied: boolean;
}
export interface CommerceCustomerRegistrationConsent {
    readonly id: string;
    readonly label: string;
    readonly description: string | null;
    readonly required: boolean;
    readonly contentHtml?: string | null;
}
export interface CommerceCustomerRegistrationField {
    readonly id: string;
    readonly label: string;
    readonly description: string | null;
    readonly inputType: "text" | "email" | "tel" | "url";
    readonly required: boolean;
    readonly placeholder: string | null;
    readonly autocomplete: string | null;
}
export interface CommerceCustomerRegistrationConfiguration {
    readonly passwordMode: "required" | "optional" | "none";
    readonly identityFields: readonly CommerceCustomerIdentityField[];
    readonly sponsor: CommerceCustomerRegistrationSponsor;
    readonly verification: CommerceCustomerRegistrationVerification;
    readonly consents: readonly CommerceCustomerRegistrationConsent[];
    readonly customFields?: readonly CommerceCustomerRegistrationField[];
}
export interface GetCommerceCustomerRegistrationRequest {}
export interface GetCommerceCustomerRegistrationResponse {
    readonly registration: CommerceCustomerRegistrationConfiguration;
}
export interface CommerceCustomerSponsor {
    readonly id: string;
    readonly webAlias: string;
    readonly displayName: string;
}
export interface SearchCommerceCustomerSponsorsRequest {
    readonly search: string;
}
export interface SearchCommerceCustomerSponsorsResponse {
    readonly sponsors: readonly CommerceCustomerSponsor[];
}

export interface LoginCommerceCustomerRequest {
    readonly identifier: string;
    readonly password: string;
    readonly rememberMe?: boolean;
}

export interface RequestCommerceCustomerVerificationCodeRequest {
    readonly email: string;
}
export interface RequestCommerceCustomerVerificationCodeResponse {
    readonly challengeId: string;
    readonly maskedDestination: string;
    readonly expiresAt: Date;
}
export interface VerifyCommerceCustomerCodeRequest {
    readonly challengeId: string;
    readonly code: string;
    readonly rememberMe?: boolean;
}
export interface VerifyCommerceCustomerCodeResponse {
    readonly session: CommerceCustomerSession;
}

export interface LoginCommerceCustomerResponse {
    readonly session: CommerceCustomerSession;
}

export interface RefreshCommerceCustomerSessionRequest {
    readonly customerId?: string;
}

export interface RefreshCommerceCustomerSessionResponse {
    readonly session: CommerceCustomerSession;
}

export interface LogoutCommerceCustomerRequest {
    readonly customerId?: string;
}

export interface LogoutCommerceCustomerResponse {
    readonly session: CommerceCustomerSession;
}

export interface CommerceCustomerAction {
    readonly id: string;
    readonly label: string;
    readonly description: string | null;
}
export interface GetCommerceCustomerActionsRequest {}
export interface GetCommerceCustomerActionsResponse {
    readonly actions: readonly CommerceCustomerAction[];
}
export interface LaunchCommerceCustomerActionRequest {
    readonly actionId: string;
}
export interface LaunchCommerceCustomerActionResponse {
    readonly url: string;
}

export type CommerceCustomerChangeReason =
    "authenticating" | "authenticated" | "refreshed" | "expired" | "logged-out";

export interface CommerceCustomerChangedEvent {
    readonly reason: CommerceCustomerChangeReason;
    readonly session: CommerceCustomerSession;
}

export type CommerceCustomerChangedListener = (event: CommerceCustomerChangedEvent) => void;

export interface CommerceCustomerDomain {
    readonly session: CommerceCustomerSession;
    readonly customer: CommerceCustomer | null;
    readonly isAuthenticated: boolean;
    login(
        request: LoginCommerceCustomerRequest,
        options?: CommerceOperationOptions,
    ): Promise<CommerceCustomerSession>;
    resolveIdentity(
        request: ResolveCommerceCustomerIdentityRequest,
        options?: CommerceOperationOptions,
    ): Promise<ResolveCommerceCustomerIdentityResponse>;
    create(
        request: CreateCommerceCustomerRequest,
        options?: CommerceOperationOptions,
    ): Promise<CommerceCustomerSession>;
    getRegistration(
        options?: CommerceOperationOptions,
    ): Promise<CommerceCustomerRegistrationConfiguration>;
    searchSponsors(
        search: string,
        options?: CommerceOperationOptions,
    ): Promise<readonly CommerceCustomerSponsor[]>;
    requestVerificationCode(
        email: string,
        options?: CommerceOperationOptions,
    ): Promise<RequestCommerceCustomerVerificationCodeResponse>;
    verifyCode(
        request: VerifyCommerceCustomerCodeRequest,
        options?: CommerceOperationOptions,
    ): Promise<CommerceCustomerSession>;
    logout(options?: CommerceOperationOptions): Promise<CommerceCustomerSession>;
    refreshSession(options?: CommerceOperationOptions): Promise<CommerceCustomerSession>;
    getActions(options?: CommerceOperationOptions): Promise<readonly CommerceCustomerAction[]>;
    launchAction(actionId: string, options?: CommerceOperationOptions): Promise<string>;
    onChanged(listener: CommerceCustomerChangedListener): CommerceEventUnsubscribe;
}

/* -------------------------------------------------------------------------- */
/* Enrollment journey                                                        */
/* -------------------------------------------------------------------------- */

export type CommerceEnrollmentStageKind =
    | "kit-selection"
    | "catalogue"
    | "recurring-offer"
    | "custom"
    | "checkout"
    | "post-order-offer"
    | "complete";
export interface CommerceEnrollmentEligibility {
    readonly eligible: boolean;
    readonly code: string | null;
    readonly message: string | null;
}
export interface CommerceEnrollmentProgress {
    readonly current: number;
    readonly total: number;
    readonly label: string;
}
export interface CommerceEnrollmentStagePresentation {
    readonly eyebrow: string;
    readonly title: string;
    readonly description: string;
    readonly primaryActionLabel: string;
    readonly secondaryActionLabel: string | null;
    readonly imageUrl: string | null;
    readonly accentColor: string | null;
}
export interface CommerceEnrollmentStage {
    readonly id: string;
    readonly kind: CommerceEnrollmentStageKind;
    readonly presentation: CommerceEnrollmentStagePresentation;
    readonly progress: CommerceEnrollmentProgress;
    readonly allowedOutcomes: readonly string[];
    readonly skippable: boolean;
    readonly catalogueId: string | null;
}
export interface CommerceEnrollmentKit {
    readonly id: string;
    readonly product: CommerceProductSummary;
    readonly badge: string | null;
    readonly highlights: readonly string[];
    readonly available: boolean;
}
export interface CommerceEnrollmentCartLine {
    readonly line: CommerceCartLine;
    readonly role: "kit" | "required" | "optional";
    readonly quantityLocked: boolean;
    readonly removalLocked: boolean;
    readonly lockReason: string | null;
}
export interface CommerceEnrollmentCart {
    readonly id: string;
    readonly version: number;
    readonly currencyCode: string;
    readonly lines: readonly CommerceEnrollmentCartLine[];
    readonly totals: CommerceCartTotals;
}
export interface CommerceEnrollmentSession {
    readonly id: string;
    readonly version: number;
    readonly status: "active" | "ineligible" | "checkout" | "completed" | "abandoned";
    readonly marketId: string;
    readonly eligibility: CommerceEnrollmentEligibility;
    readonly currentStage: CommerceEnrollmentStage;
    readonly stages: readonly CommerceEnrollmentStage[];
    readonly categories: readonly CommerceProductCategory[];
    readonly kits: readonly CommerceEnrollmentKit[];
    readonly selectedKitId: string | null;
    readonly cart: CommerceEnrollmentCart;
    readonly createdAt: Date;
    readonly updatedAt: Date;
}
export type BeginCommerceEnrollmentRequest = Record<string, never>;
export interface BeginCommerceEnrollmentResponse {
    readonly enrollment: CommerceEnrollmentSession;
}
export interface GetCommerceEnrollmentRequest {
    readonly enrollmentId?: string;
}
export interface GetCommerceEnrollmentResponse {
    readonly enrollment: CommerceEnrollmentSession | null;
}
export interface SelectCommerceEnrollmentKitRequest {
    readonly enrollmentId: string;
    readonly kitId: string;
    readonly expectedVersion: number;
}
export interface SelectCommerceEnrollmentKitResponse {
    readonly enrollment: CommerceEnrollmentSession;
}
export interface AddCommerceEnrollmentProductRequest {
    readonly enrollmentId: string;
    readonly productId: string;
    readonly variantId?: string;
    readonly quantity: number;
    readonly purchaseType: CommerceCartPurchaseType;
    readonly frequencyId?: string;
    /** @deprecated Use frequencyId. */
    readonly frequencyDays?: number;
    readonly expectedVersion: number;
}
export interface AddCommerceEnrollmentProductResponse {
    readonly enrollment: CommerceEnrollmentSession;
}
export interface CompleteCommerceEnrollmentStageRequest {
    readonly enrollmentId: string;
    readonly stageId: string;
    readonly outcome: string;
    readonly expectedVersion: number;
}
export interface CompleteCommerceEnrollmentStageResponse {
    readonly enrollment: CommerceEnrollmentSession;
}
export type ReturnCommerceEnrollmentToShoppingRequest = Record<string, never>;
export interface ReturnCommerceEnrollmentToShoppingResponse {
    readonly enrollment: CommerceEnrollmentSession;
}
export interface AbandonCommerceEnrollmentRequest {
    readonly enrollmentId: string;
    readonly expectedVersion?: number;
}
export interface AbandonCommerceEnrollmentResponse {
    readonly abandoned: boolean;
}
export type CommerceEnrollmentChangeReason =
    | "started"
    | "resumed"
    | "cart-changed"
    | "kit-selected"
    | "product-added"
    | "stage-completed"
    | "returned-to-shopping"
    | "customer-logged-out"
    | "abandoned";
export interface CommerceEnrollmentChangedEvent {
    readonly reason: CommerceEnrollmentChangeReason;
    readonly enrollment: CommerceEnrollmentSession | null;
}
export type CommerceEnrollmentChangedListener = (event: CommerceEnrollmentChangedEvent) => void;
export interface CommerceEnrollmentDomain {
    readonly current: CommerceEnrollmentSession | null;
    begin(
        request: BeginCommerceEnrollmentRequest,
        options?: CommerceOperationOptions,
    ): Promise<CommerceEnrollmentSession>;
    resume(
        enrollmentId?: string,
        options?: CommerceOperationOptions,
    ): Promise<CommerceEnrollmentSession>;
    restoreIfAvailable(
        options?: CommerceOperationOptions,
    ): Promise<CommerceEnrollmentSession | null>;
    selectKit(
        kitId: string,
        options?: CommerceOperationOptions,
    ): Promise<CommerceEnrollmentSession>;
    addProduct(
        request: Omit<AddCommerceEnrollmentProductRequest, "enrollmentId" | "expectedVersion">,
        options?: CommerceOperationOptions,
    ): Promise<CommerceEnrollmentSession>;
    completeStage(
        outcome: string,
        options?: CommerceOperationOptions,
    ): Promise<CommerceEnrollmentSession>;
    returnToShopping(options?: CommerceOperationOptions): Promise<CommerceEnrollmentSession>;
    abandon(options?: CommerceOperationOptions): Promise<void>;
    onChanged(listener: CommerceEnrollmentChangedListener): CommerceEventUnsubscribe;
}

/* -------------------------------------------------------------------------- */
/* Storefront identity and social sharing                                    */
/* -------------------------------------------------------------------------- */

export type CommerceShareTargetType =
    | "storefront"
    | "contact"
    | "product"
    | "cart"
    | "collection"
    | "routine"
    | "campaign"
    | "coupon"
    | "page";

export type CommerceBuiltInShareChannel =
    | "native"
    | "copy"
    | "email"
    | "sms"
    | "whatsapp"
    | "facebook"
    | "messenger"
    | "x"
    | "linkedin"
    | "pinterest";

export type CommerceShareChannel = CommerceBuiltInShareChannel | (string & {});

export interface CommerceContactMethods {
    readonly email: string | null;
    readonly phone: string | null;
    readonly websiteUrl: string | null;
    readonly messageUrl: string | null;
    readonly vCardUrl: string | null;
}

export interface CommerceStorefrontLocation {
    readonly city: string | null;
    readonly region: string | null;
    readonly countryCode: string | null;
    readonly display: string | null;
}

export interface CommerceStorefrontContact {
    readonly id: string;
    readonly webAlias: string;
    readonly displayName: string;
    readonly role: string;
    readonly biography: string;
    readonly avatar: CommerceImage | null;
    readonly location: CommerceStorefrontLocation | null;
    readonly methods: CommerceContactMethods;
}

export type CommerceStorefrontStatus = "generic" | "resolved" | "unavailable";

export interface CommerceAttribution {
    readonly referrerId: string | null;
    readonly webAlias: string | null;
    readonly campaign: string | null;
    readonly referrer: string | null;
    readonly landingPage: string | null;
}

export interface CommerceStorefrontExperience {
    readonly status: CommerceStorefrontStatus;
    readonly contact: CommerceStorefrontContact | null;
    readonly attribution: CommerceAttribution;
    readonly requestedAlias: string | null;
    readonly notice: string | null;
    readonly marketId: string;
    readonly currencyCode: string;
    readonly languageCode: string;
    readonly resolvedAt: Date;
}

export interface ResolveCommerceStorefrontRequest {
    readonly url?: string;
    readonly webAlias?: string;
    readonly referrerId?: string;
    readonly campaign?: string;
    readonly referrer?: string;
}

export interface ResolveCommerceStorefrontResponse {
    readonly storefront: CommerceStorefrontExperience;
}

export interface ClearCommerceStorefrontRequest {}
export interface ClearCommerceStorefrontResponse {
    readonly storefront: CommerceStorefrontExperience;
}

export interface CommerceShareTarget {
    readonly type: CommerceShareTargetType;
    readonly id?: string;
    readonly title?: string;
    readonly description?: string;
    readonly imageUrl?: string;
    readonly url?: string;
    readonly metadata?: Readonly<Record<string, string>>;
}

export interface CreateCommerceShareResourceRequest {
    readonly target: CommerceShareTarget;
    readonly channel?: CommerceShareChannel;
}

export interface CommerceShareResource {
    readonly id: string;
    readonly target: CommerceShareTarget;
    readonly url: string;
    readonly shortUrl: string;
    readonly deepLink: string | null;
    readonly title: string;
    readonly description: string;
    readonly text: string;
    readonly imageUrl: string | null;
    readonly qrCodeUrl: string;
    readonly channelUrls: Readonly<Partial<Record<CommerceShareChannel, string>>>;
    readonly createdAt: Date;
    readonly expiresAt: Date | null;
    readonly metadata: Readonly<Record<string, string>>;
}

export interface CreateCommerceShareResourceResponse {
    readonly share: CommerceShareResource;
}

export type CommerceShareAcceptanceStrategy = "merge" | "replace";

export interface CommerceSharePreviewItem {
    readonly productId: string;
    readonly variantId?: string;
    readonly name: string;
    readonly image: CommerceImage | null;
    readonly quantity: number;
    readonly purchaseType: CommerceCartPurchaseType;
    readonly unitPrice: CommerceMoney;
}

export interface CommerceSharePreview {
    readonly id: string;
    readonly resource: CommerceShareResource;
    /** The authoritative purchase journey restored when this cart share is accepted. */
    readonly experience: CommerceCheckoutExperience | null;
    /** Strategies the Gateway permits for this exact shared resource. */
    readonly acceptanceStrategies: readonly CommerceShareAcceptanceStrategy[];
    readonly contact: CommerceStorefrontContact | null;
    readonly items: readonly CommerceSharePreviewItem[];
    readonly subtotal: CommerceMoney | null;
    readonly itemCount: number;
    readonly canAccept: boolean;
    readonly expiresAt: Date | null;
}

export interface ResolveCommerceShareRequest {
    readonly shareId: string;
}
export interface ResolveCommerceShareResponse {
    readonly preview: CommerceSharePreview;
}
export interface AcceptCommerceShareRequest {
    readonly shareId: string;
    readonly strategy: CommerceShareAcceptanceStrategy;
}
export interface AcceptCommerceShareResponse {
    readonly preview: CommerceSharePreview;
    readonly cart: CommerceCart;
}

export interface CommerceShareProvider {
    readonly channel: CommerceShareChannel;
    readonly label: string;
    readonly priority?: number;
    readonly isAvailable?: () => boolean;
    createUrl(resource: CommerceShareResource): string | null;
}

export type CommerceShareEventName =
    | "created"
    | "copied"
    | "native"
    | "provider"
    | "qr"
    | "downloaded"
    | "completed"
    | "resolved"
    | "accepted";

export interface CommerceShareEvent {
    readonly name: CommerceShareEventName;
    readonly resource: CommerceShareResource;
    readonly channel?: CommerceShareChannel;
}

export type CommerceShareListener = (event: CommerceShareEvent) => void;

export interface CommerceShareDomain {
    readonly providers: readonly CommerceShareProvider[];
    create(
        target: CommerceShareTarget,
        options?: CommerceOperationOptions,
    ): Promise<CommerceShareResource>;
    storefront(options?: CommerceOperationOptions): Promise<CommerceShareResource>;
    product(productId: string, options?: CommerceOperationOptions): Promise<CommerceShareResource>;
    cart(options?: CommerceOperationOptions): Promise<CommerceShareResource>;
    collection(
        collectionId: string,
        options?: CommerceOperationOptions,
    ): Promise<CommerceShareResource>;
    routine(routineId: string, options?: CommerceOperationOptions): Promise<CommerceShareResource>;
    campaign(
        campaignId: string,
        options?: CommerceOperationOptions,
    ): Promise<CommerceShareResource>;
    coupon(couponCode: string, options?: CommerceOperationOptions): Promise<CommerceShareResource>;
    contact(options?: CommerceOperationOptions): Promise<CommerceShareResource>;
    page(
        url: string,
        title?: string,
        options?: CommerceOperationOptions,
    ): Promise<CommerceShareResource>;
    registerProvider(provider: CommerceShareProvider): CommerceEventUnsubscribe;
    getProvider(channel: CommerceShareChannel): CommerceShareProvider | undefined;
    destinationUrl(resource: CommerceShareResource, channel: CommerceShareChannel): string | null;
    copy(resource: CommerceShareResource): Promise<boolean>;
    native(resource: CommerceShareResource): Promise<boolean>;
    resolve(shareId: string, options?: CommerceOperationOptions): Promise<CommerceSharePreview>;
    accept(
        shareId: string,
        strategy?: CommerceShareAcceptanceStrategy,
        options?: CommerceOperationOptions,
    ): Promise<CommerceCart>;
    onEvent(listener: CommerceShareListener): CommerceEventUnsubscribe;
}

export type CommerceStorefrontChangeReason = "resolved" | "unavailable" | "changed" | "cleared";
export interface CommerceStorefrontChangedEvent {
    readonly reason: CommerceStorefrontChangeReason;
    readonly storefront: CommerceStorefrontExperience;
}
export type CommerceStorefrontChangedListener = (event: CommerceStorefrontChangedEvent) => void;
export interface CommerceStorefrontDomain {
    readonly current: CommerceStorefrontExperience;
    resolve(
        request?: ResolveCommerceStorefrontRequest,
        options?: CommerceOperationOptions,
    ): Promise<CommerceStorefrontExperience>;
    resolveLocation(
        location?: string | URL,
        attribution?: Omit<ResolveCommerceStorefrontRequest, "url" | "webAlias">,
        options?: CommerceOperationOptions,
    ): Promise<CommerceStorefrontExperience>;
    clearAttribution(options?: CommerceOperationOptions): Promise<CommerceStorefrontExperience>;
    onChanged(listener: CommerceStorefrontChangedListener): CommerceEventUnsubscribe;
}

/* -------------------------------------------------------------------------- */
/* Operation execution                                                        */
/* -------------------------------------------------------------------------- */

export interface CommerceOperationOptions {
    readonly signal?: AbortSignal;
    readonly bypassCache?: boolean;
}

/* -------------------------------------------------------------------------- */
/* Diagnostics                                                                */
/* -------------------------------------------------------------------------- */

export type CommerceRuntimeInitializationStageName =
    "configuration" | "session" | "context" | "validation";

export interface CommerceRuntimeInitializationStage {
    readonly name: CommerceRuntimeInitializationStageName;
    readonly durationMilliseconds: number;
}

export interface CommerceRuntimeDiagnostics {
    readonly initializedAt: Date;
    readonly initializationDurationMilliseconds: number;
    readonly transportName: string;
    readonly correlationId: string;
    readonly stages: readonly CommerceRuntimeInitializationStage[];
}

/* -------------------------------------------------------------------------- */
/* Unified runtime events                                                     */
/* -------------------------------------------------------------------------- */

export type CommerceEvent =
    | {
          readonly name: "context.changed";
          readonly occurredAt: Date;
          readonly data: CommerceContextChangedEvent;
      }
    | {
          readonly name: "cart.changed";
          readonly occurredAt: Date;
          readonly data: CommerceCartChangedEvent;
      }
    | {
          readonly name: "checkout.changed";
          readonly occurredAt: Date;
          readonly data: CommerceCheckoutChangedEvent;
      }
    | {
          readonly name: "payment.changed";
          readonly occurredAt: Date;
          readonly data: CommercePaymentChangedEvent;
      }
    | {
          readonly name: "order.changed";
          readonly occurredAt: Date;
          readonly data: CommerceOrderChangedEvent;
      }
    | {
          readonly name: "customer.changed";
          readonly occurredAt: Date;
          readonly data: CommerceCustomerChangedEvent;
      }
    | {
          readonly name: "enrollment.changed";
          readonly occurredAt: Date;
          readonly data: CommerceEnrollmentChangedEvent;
      }
    | {
          readonly name: "storefront.changed";
          readonly occurredAt: Date;
          readonly data: CommerceStorefrontChangedEvent;
      }
    | {
          readonly name: "share.event";
          readonly occurredAt: Date;
          readonly data: CommerceShareEvent;
      }
    | {
          readonly name: "runtime.disposed";
          readonly occurredAt: Date;
          readonly data: CommerceDisposedEvent;
      };

export type CommerceEventName = CommerceEvent["name"];
export type CommerceEventOf<TName extends CommerceEventName> = Extract<
    CommerceEvent,
    { readonly name: TName }
>;
export type CommerceEventListener = (event: CommerceEvent) => void;
export type CommerceNamedEventListener<TName extends CommerceEventName> = (
    event: CommerceEventOf<TName>,
) => void;

export interface CommerceEventsDomain {
    on(listener: CommerceEventListener): CommerceEventUnsubscribe;
    on<TName extends CommerceEventName>(
        name: TName,
        listener: CommerceNamedEventListener<TName>,
    ): CommerceEventUnsubscribe;
}

/* -------------------------------------------------------------------------- */
/* Errors                                                                     */
/* -------------------------------------------------------------------------- */

export type CommerceErrorCode =
    | "configuration-required"
    | "configuration-invalid"
    | "session-bootstrap-failed"
    | "session-invalid"
    | "context-bootstrap-failed"
    | "context-invalid"
    | "context-switch-failed"
    | "product-search-failed"
    | "product-response-invalid"
    | "product-not-found"
    | "product-category-not-found"
    | "cart-operation-failed"
    | "cart-response-invalid"
    | "cart-line-not-found"
    | "cart-coupon-invalid"
    | "cart-version-conflict"
    | "checkout-operation-failed"
    | "checkout-response-invalid"
    | "checkout-not-found"
    | "checkout-empty-cart"
    | "checkout-address-invalid"
    | "checkout-shipping-invalid"
    | "checkout-version-conflict"
    | "payment-not-configured"
    | "payment-operation-failed"
    | "payment-cancelled"
    | "payment-response-invalid"
    | "order-operation-failed"
    | "order-response-invalid"
    | "order-id-invalid"
    | "order-completion-timeout"
    | "order-polling-cancelled"
    | "customer-authentication-failed"
    | "customer-identity-resolution-failed"
    | "customer-creation-failed"
    | "customer-session-refresh-failed"
    | "customer-session-invalid"
    | "enrollment-operation-failed"
    | "enrollment-response-invalid"
    | "enrollment-not-found"
    | "enrollment-validation-failed"
    | "storefront-operation-failed"
    | "storefront-response-invalid"
    | "share-operation-failed"
    | "share-target-invalid"
    | "runtime-disposed";

/* -------------------------------------------------------------------------- */
/* Lifecycle                                                                  */
/* -------------------------------------------------------------------------- */

export interface CommerceDisposedEvent {
    readonly disposedAt: Date;
}

export type CommerceEventUnsubscribe = () => void;

export type CommerceDisposedListener = (event: CommerceDisposedEvent) => void;

export interface CommerceLifecycle {
    readonly isReady: boolean;
    readonly isDisposed: boolean;

    onDisposed(listener: CommerceDisposedListener): CommerceEventUnsubscribe;
}

/* -------------------------------------------------------------------------- */
/* Public runtime                                                             */
/* -------------------------------------------------------------------------- */

export interface Commerce {
    readonly version: string;
    readonly configuration: ResolvedCommerceConfiguration;
    readonly session: CommerceSession;
    readonly context: CommerceContext;
    readonly market: CommerceMarketDomain;
    readonly diagnostics: CommerceRuntimeDiagnostics;
    readonly lifecycle: CommerceLifecycle;
    readonly events: CommerceEventsDomain;
    readonly products: CommerceProductCatalogue;
    readonly cart: CommerceCartDomain;
    readonly checkout: CommerceCheckoutDomain;
    readonly payment: CommercePaymentDomain;
    readonly orders: CommerceOrderDomain;
    readonly customer: CommerceCustomerDomain;
    readonly enrollment: CommerceEnrollmentDomain;
    readonly storefront: CommerceStorefrontDomain;
    readonly share: CommerceShareDomain;

    dispose(): void;
}
